import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getBaseURL } from "../../services/api";
import { ToastProvider, useToast } from "./AdminToast";
import { S, COLORS, GLOBAL_ADMIN_CSS } from "./adminStyles";
import { parseMatchTime } from "../../utils/dateUtils";

const API_URL = getBaseURL();

const RevenueAnalyticsInner = () => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("adminToken");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    if (!token) navigate("/winrox11-super-admin");
    else fetchAnalytics();
  }, [token, navigate]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/revenue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      toast.error("Failed to load revenue data.");
    } finally {
      setLoading(false);
    }
  };

  const totalPlatformProfit = analytics.reduce((sum, item) => sum + Number(item.net_profit || 0), 0);
  const totalCollections = analytics.reduce((sum, item) => sum + Number(item.total_collection || 0), 0);

  return (
    <div style={S.page}>
      <style>{GLOBAL_ADMIN_CSS}</style>
      <button onClick={() => navigate("/winrox11-super-admin/dashboard")} style={S.backBtn}>← Back to Dashboard</button>

      <div style={S.topBar}>
        <div>
          <div style={S.pageTitle}>📊 Revenue Analytics</div>
          <div style={S.pageSubtitle}>Financial performance and profit tracking</div>
        </div>
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.green }}>₹{totalPlatformProfit.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Net Profit</div>
          </div>
          <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "12px", padding: "12px 18px", textAlign: "center" }}>
            <div style={{ fontSize: "22px", fontWeight: 800, color: COLORS.indigo }}>₹{totalCollections.toLocaleString("en-IN")}</div>
            <div style={{ fontSize: "10px", color: COLORS.textMuted, fontWeight: 700, letterSpacing: "1px", textTransform: "uppercase" }}>Collections</div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={S.loading}>Calculating financial data...</div>
      ) : (
        <div style={{ background: COLORS.card, border: `1px solid ${COLORS.cardBorder}`, borderRadius: "16px", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "900px" }}>
              <thead>
                <tr style={{ background: COLORS.inputBg }}>
                  <th style={{ padding: "14px 16px", fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, letterSpacing: "1px", textTransform: "uppercase", textAlign: "left" }}>Match</th>
                  <th style={{ padding: "14px 16px", fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, letterSpacing: "1px", textTransform: "uppercase", textAlign: "center" }}>Contest Details</th>
                  <th style={{ padding: "14px 16px", fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, letterSpacing: "1px", textTransform: "uppercase", textAlign: "right" }}>Collection</th>
                  <th style={{ padding: "14px 16px", fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, letterSpacing: "1px", textTransform: "uppercase", textAlign: "right" }}>Prize Pool</th>
                  <th style={{ padding: "14px 16px", fontSize: "10px", fontWeight: 700, color: COLORS.textMuted, letterSpacing: "1px", textTransform: "uppercase", textAlign: "right" }}>Net Profit</th>
                </tr>
              </thead>
              <tbody>
                {analytics.length === 0 ? (
                  <tr><td colSpan="5" style={{ padding: "40px", textAlign: "center", color: COLORS.textMuted }}>No active or completed contests found.</td></tr>
                ) : (
                  analytics.map((row, index) => (
                    <tr key={index} className="admin-row-hover" style={{ borderBottom: `1px solid ${COLORS.cardBorder}` }}>
                      <td style={{ padding: "14px 16px" }}>
                        <div style={{ fontWeight: 700, color: COLORS.text }}>{row.team1} vs {row.team2}</div>
                        <div style={{ fontSize: "11px", color: COLORS.textMuted }}>{row.format} • {parseMatchTime(row).toLocaleDateString()}</div>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "center" }}>
                        <div style={{ fontSize: "14px", fontWeight: 700, color: COLORS.textMid }}>{row.filled_spots} / {row.spots}</div>
                        <div style={{ fontSize: "11px", color: COLORS.textMuted }}>₹{row.entry_fee} Entry</div>
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: 700, color: COLORS.indigoLight }}>
                        ₹{Number(row.total_collection).toLocaleString("en-IN")}
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right", fontWeight: 700, color: COLORS.red }}>
                        ₹{Number(row.prize_pool).toLocaleString("en-IN")}
                      </td>
                      <td style={{ padding: "14px 16px", textAlign: "right" }}>
                        <div style={{ 
                          fontSize: "16px", 
                          fontWeight: 800, 
                          color: Number(row.net_profit) >= 0 ? COLORS.green : COLORS.red 
                        }}>
                          {Number(row.net_profit) < 0 ? "-" : ""}₹{Math.abs(Number(row.net_profit)).toLocaleString("en-IN")}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: "24px", padding: "16px", background: COLORS.green + "10", border: `1px solid ${COLORS.green}20`, borderRadius: "12px", fontSize: "12px", color: COLORS.textMuted, textAlign: "center" }}>
        💡 <strong>Net Profit</strong> is calculated as (Total Collections - Total Prize Pool). This excludes bonuses and referral costs.
      </div>
    </div>
  );
};

const RevenueAnalytics = () => (
  <ToastProvider>
    <RevenueAnalyticsInner />
  </ToastProvider>
);

export default RevenueAnalytics;
